import React, {
  useEffect,
  useState,
  useContext,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useCallback
} from "react";
import { Box } from "@mui/material";
import { AuthContext } from "../contexts/AuthContext";
import { useErrorDialog } from "../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../contexts/SuccessDialogContext";
import ComponentLoading from "../components/App/ComponentLoading";
import { debounce } from "lodash";
import Notes from "../components/Schedules/utils/Notes";
import CalculationSummary from "../components/MainTaxComputation/CalculationSummary";
import MainComputationCalculator from "../builders/calculator/mainComputation";
import Header from "../components/MainTaxComputation/Header";
import AdjustmentDetails from "../components/MainTaxComputation/AdjustmentDetails";
import UnutilisedDetails from "../components/MainTaxComputation/UnutilisedDetails";
import PriorYAExcelUpload from "../components/MainTaxComputation/PriorYAExcelUpload";
import TaxExemptionDetails from "../components/MainTaxComputation/TaxExemptionDetails";
import CorporateRebate from "../components/MainTaxComputation/CorporateRebate";
import TaxPreviouslyAssessed from "../components/MainTaxComputation/TaxPreviouslyAssessed";
import SectionNavigation from "../components/MainTaxComputation/SectionNavigation";
import PdfGenerator from "../components/MainTaxComputation/PdfGenerator";
import { getCommaOut } from "../utils/hooks/dataHandler";
import { MAIN_COMPUTATION } from "../utils/data/controlData";
import { useAccessControl } from "../utils/hooks/useAccessControl";
import AccessDenied from "../components/Common/AccessDenied";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  getCrossYAApportionmentContext,
  apportionCrossYARows,
  reconcileCrossYAAmounts,
  applyCrossYAOffset
} from "../components/MainTaxComputation/utils/precedingCalculation";
import {
  buildYABaseComputation,
  buildYASummary
} from "../components/MainTaxComputation/CrossYACalculationSummary";
import {
  calculateTaxPreviouslyAssessedTotal,
  getSingleYAAmount,
  getTaxPreviouslyAssessedAmount,
  getUnutilisedAmount,
  hasSplitAmountFields,
  omitCrossYARowFields,
  sumCalculatorValues,
  getSignedUnutilisedLossAmount
} from "../components/MainTaxComputation/Pdf/shared";
import { buildIrasMainCompSections } from "../components/MainTaxComputation/Pdf/iras/buildIrasMainCompSections";
import { resolveIrasChargeableIncomeSummary } from "../components/MainTaxComputation/Pdf/iras/chargeableIncomeSummary";
import {
  calculateIrasSgdSectionResults,
  getRenderableIrasMainCompSections
} from "../components/MainTaxComputation/Pdf/iras/sgdCalculation";

const TAX_ADJUSTMENTS = "Tax Adjustments";
const TAX_ADJUSTMENT_SEPARATE_SOURCED_INCOME =
  "Tax adjustment (separate sourced income)";
const TAX_ADJUSTMENT_BALANCING_CHARGE = "Tax adjustment (balancing charge)";
const NET_INVESTMENT_INCOME = "Net Investment Income";
const FURTHER_DEDUCTION_OTHER = "Further deduction other";
const FURTHER_DEDUCTION_S14N = "Further deduction s14n";
const CURRENT_CA = "Current Year Capital Allowances";
const UN_CA = "Unutilised Capital Allowances";
const UN_LOSS = "Unutilised Losses";
const UN_DONATION = "Unutilised Donations";
const CURRENT_DONATION = "Current Year Donations";
const CURRENT_YEAR_INVESTMENT_LOSSES = "Current Year Investment Losses";
const OTHER_INCOME = "Other Source Income";
const TAX_EXEMP = "Partial Tax Exemption / Full Tax Exemption";
const COR_TAX_REBATE = "Corporate Income Tax Rebate";
const TAX_PREV_ASSESS = "Tax Previously Assessed";
const TAX_ADJUSTMENTS_GROUP = "tax_adjustments_group";
const FURTHER_DEDUCTION_GROUP = "further_deduction_group";

const normalizeSingleYARow = (row) => {
  if (!row) return row;

  return {
    ...omitCrossYARowFields(row),
    amount: getSingleYAAmount(row)
  };
};

const normalizeSingleYARows = (rows = []) =>
  Array.isArray(rows) ? rows.map(normalizeSingleYARow) : [];

const normalizeRowsForTaxPeriod = (
  rows = [],
  isCrossYA,
  apportionmentContext
) =>
  isCrossYA
    ? apportionCrossYARows(rows, apportionmentContext)
    : normalizeSingleYARows(rows);

const normalizeReferenceDataForTaxPeriod = (
  referenceData = {},
  isCrossYA,
  apportionmentContext
) => ({
  taxAdjustments: normalizeRowsForTaxPeriod(
    referenceData.taxAdjustments,
    isCrossYA,
    apportionmentContext
  ),
  taxAdjustmentSeparateSourcedIncome: normalizeRowsForTaxPeriod(
    referenceData.taxAdjustmentSeparateSourcedIncome,
    isCrossYA,
    apportionmentContext
  ),
  taxAdjustmentBalancingCharge: normalizeRowsForTaxPeriod(
    referenceData.taxAdjustmentBalancingCharge,
    isCrossYA,
    apportionmentContext
  ),
  furtherDeductionS14n: normalizeRowsForTaxPeriod(
    referenceData.furtherDeductionS14n,
    isCrossYA,
    apportionmentContext
  ),
  furtherDeductionOther: normalizeRowsForTaxPeriod(
    referenceData.furtherDeductionOther,
    isCrossYA,
    apportionmentContext
  ),
  currentYearCapitalAllowances: normalizeRowsForTaxPeriod(
    referenceData.currentYearCapitalAllowances,
    isCrossYA,
    apportionmentContext
  ),
  otherSourceIncome: normalizeRowsForTaxPeriod(
    referenceData.otherSourceIncome,
    isCrossYA,
    apportionmentContext
  ),
  currentYearDonations: normalizeRowsForTaxPeriod(
    referenceData.currentYearDonations,
    isCrossYA,
    apportionmentContext
  ),
  currentYearInvestmentLosses: normalizeRowsForTaxPeriod(
    referenceData.currentYearInvestmentLosses,
    isCrossYA,
    apportionmentContext
  )
});

const hasVisibleSingleYATaxPreviouslyAssessedValue = (row) =>
  !hasSplitAmountFields(row) &&
  (Boolean(row?.date) || getSingleYAAmount(row) !== 0);

const normalizeSingleYATaxPreviouslyAssessedRows = (rows = []) => {
  const allRows = Array.isArray(rows) ? rows : [];
  const autoloadRows = normalizeSingleYARows(
    allRows.filter((row) => row?.status === "autoload")
  );
  const manualRows = allRows.filter((row) => row?.status !== "autoload");
  const visibleSingleYARow = manualRows.find(
    hasVisibleSingleYATaxPreviouslyAssessedValue
  );

  if (visibleSingleYARow) {
    return [normalizeSingleYARow(visibleSingleYARow), ...autoloadRows];
  }

  if (manualRows.length === 0) {
    return [{ date: "", amount: 0, historyAmount: [] }, ...autoloadRows];
  }

  const amount = manualRows.reduce(
    (sum, row) => sum + getSingleYAAmount(row),
    0
  );
  const currentYARow =
    manualRows.find((row) => parseFloat(row?.current_amount) || 0) ||
    manualRows.find((row) => row?.date) ||
    manualRows[0];

  return [
    normalizeSingleYARow({
      ...currentYARow,
      amount,
      historyAmount: [amount]
    }),
    ...autoloadRows
  ];
};

const normalizeManualCrossYAUnutilisedRow = (row) => {
  if (!row || !hasSplitAmountFields(row)) return row;

  const fallbackAmount = sumCalculatorValues(
    Array.isArray(row?.historyAmount) && row.historyAmount.length > 0
      ? row.historyAmount
      : row?.amount
  );
  const precedingAmount = parseFloat(row?.preceding_amount) || 0;
  const currentAmount = parseFloat(row?.current_amount) || 0;
  const splitAmount = precedingAmount + currentAmount;

  if (splitAmount === 0 && fallbackAmount !== 0) {
    return {
      ...row,
      amount: fallbackAmount,
      preceding_amount: 0,
      current_amount: fallbackAmount,
      historyAmount: [fallbackAmount],
      precedingHistoryAmount: [0],
      currentHistoryAmount:
        Array.isArray(row?.historyAmount) && row.historyAmount.length > 0
          ? row.historyAmount
          : [fallbackAmount],
      crossYAOffset: 0,
      crossYAOffsetAppliedTo: "",
      crossYAMismatchAmount: 0
    };
  }

  return {
    ...row,
    amount: splitAmount,
    preceding_amount: precedingAmount,
    current_amount: currentAmount,
    historyAmount: [splitAmount],
    precedingHistoryAmount: row?.precedingHistoryAmount ?? [precedingAmount],
    currentHistoryAmount: row?.currentHistoryAmount ?? [currentAmount],
    crossYAOffset: 0,
    crossYAOffsetAppliedTo: "",
    crossYAMismatchAmount: 0
  };
};

const defaultUnutilisedAmountToCurrentYA = (row, isCrossYA) => {
  if (!isCrossYA) return row;

  const amount = sumCalculatorValues(
    Array.isArray(row?.historyAmount) && row.historyAmount.length > 0
      ? row.historyAmount
      : row?.amount
  );

  if (hasSplitAmountFields(row)) {
    return normalizeManualCrossYAUnutilisedRow(row);
  }

  return normalizeManualCrossYAUnutilisedRow({
    ...row,
    amount,
    preceding_amount: 0,
    current_amount: amount,
    precedingHistoryAmount: [0],
    currentHistoryAmount:
      Array.isArray(row?.historyAmount) && row.historyAmount.length > 0
        ? row.historyAmount
        : [amount]
  });
};

const adjustmentGroupTitle = {
  A: "Tax Adjustments",
  B: "Further Deductions"
};

// Common company sections
const CommonPageSections = {
  A: [
    {
      id: "tax_adjustments",
      parentId: TAX_ADJUSTMENTS_GROUP,
      value: TAX_ADJUSTMENTS,
      label: "General",
      totalId: "TAX_ADJUSTMENTS"
    },
    {
      id: "tax_adjustment_separate_sourced_income",
      parentId: TAX_ADJUSTMENTS_GROUP,
      value: TAX_ADJUSTMENT_SEPARATE_SOURCED_INCOME,
      label: "Separate Sourced Income",
      totalId: "TAX_ADJUSTMENT_SEPARATE_SOURCED_INCOME"
    },
    {
      id: "tax_adjustment_balancing_charge",
      parentId: TAX_ADJUSTMENTS_GROUP,
      value: TAX_ADJUSTMENT_BALANCING_CHARGE,
      label: "Balancing Charge",
      totalId: "TAX_ADJUSTMENT_BALANCING_CHARGE"
    }
  ],
  B: [
    {
      id: "further_deduction_s14n",
      parentId: FURTHER_DEDUCTION_GROUP,
      value: FURTHER_DEDUCTION_S14N,
      label: "Further Deduction (S14N)",
      totalId: "FURTHER_DEDUCTION_S14N"
    },
    {
      id: "further_deduction_other",
      parentId: FURTHER_DEDUCTION_GROUP,
      value: FURTHER_DEDUCTION_OTHER,
      label: "Other further deduction",
      totalId: "FURTHER_DEDUCTION_OTHER"
    }
  ],
  C: [
    {
      id: "current_year_capital_allowances",
      value: CURRENT_CA,
      label: CURRENT_CA,
      totalId: "CURRENT_CA"
    }
  ],
  D: [
    {
      id: "unutilised_capital_allowances",
      value: UN_CA,
      label: UN_CA,
      totalId: "UN_CA"
    },
    {
      id: "unutilised_losses",
      value: UN_LOSS,
      label: UN_LOSS,
      totalId: "UN_LOSS"
    },
    {
      id: "unutilised_donations",
      value: UN_DONATION,
      label: UN_DONATION,
      totalId: "UN_DONATION"
    }
  ],
  E: [
    {
      id: "other_source_income",
      value: OTHER_INCOME,
      label: OTHER_INCOME,
      totalId: "OTHER_INCOME"
    }
  ],
  F: [
    {
      id: "current_year_donations",
      value: CURRENT_DONATION,
      label: CURRENT_DONATION,
      totalId: "CURRENT_DONATION"
    }
  ],
  G: [
    {
      id: "partial_tax_exemption_full_tax_exemption",
      value: TAX_EXEMP,
      label: TAX_EXEMP,
      totalId: "TAX_EXEMP"
    }
  ],
  H: [
    {
      id: "corporate_income_tax_rebate",
      value: COR_TAX_REBATE,
      label: COR_TAX_REBATE,
      totalId: "COR_TAX_REBATE"
    },
    {
      id: "tax_previously_assessed",
      value: TAX_PREV_ASSESS,
      label: "Tax deduction",
      totalId: "TAX_PREV_ASSESS"
    }
  ]
};

// Investment company sections (reusing Tax Adjustments as Net Investment Income)
const InvestmentPageSections = {
  A: [
    {
      id: "tax_adjustments",
      value: TAX_ADJUSTMENTS,
      label: NET_INVESTMENT_INCOME,
      totalId: "TAX_ADJUSTMENTS"
    }
  ],
  B: [
    {
      id: "current_year_capital_allowances",
      value: CURRENT_CA,
      label: CURRENT_CA,
      totalId: "CURRENT_CA"
    },
    {
      id: "current_year_donations",
      value: CURRENT_DONATION,
      label: CURRENT_DONATION,
      totalId: "CURRENT_DONATION"
    },
    {
      id: "current_year_losses",
      value: CURRENT_YEAR_INVESTMENT_LOSSES,
      label: "Current Year Losses",
      totalId: "CURRENT_YEAR_INVESTMENT_LOSSES"
    }
  ],
  C: [
    {
      id: "partial_tax_exemption_full_tax_exemption",
      value: TAX_EXEMP,
      label: TAX_EXEMP,
      totalId: "TAX_EXEMP"
    }
  ],
  D: [
    {
      id: "corporate_income_tax_rebate",
      value: COR_TAX_REBATE,
      label: COR_TAX_REBATE,
      totalId: "COR_TAX_REBATE"
    },
    {
      id: "tax_previously_assessed",
      value: TAX_PREV_ASSESS,
      label: "Tax deduction",
      totalId: "TAX_PREV_ASSESS"
    }
  ]
};

const defaultYearRebates = [
  { year: 2025, amount: 2000 },
  { year: 2026, amount: 2000 }
];

const defaultCorporateRebateConfig = {
  isRebate: false,
  taxableRate: 0,
  cappedAmount: 0
};

const buildDefaultCorporateRebate = (isCrossYA) =>
  isCrossYA
    ? {
        preceding: { ...defaultCorporateRebateConfig },
        current: { ...defaultCorporateRebateConfig }
      }
    : { ...defaultCorporateRebateConfig };

const buildDefaultStartupConfig = (isCrossYA) =>
  isCrossYA
    ? {
        preceding: false,
        current: false
      }
    : false;

const normaliseStartupConfig = (value, isCrossYA) => {
  if (!isCrossYA) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return Boolean(value.current ?? value.preceding ?? false);
    }

    return Boolean(value);
  }

  if (value && typeof value === "object" && !Array.isArray(value)) {
    return {
      ...buildDefaultStartupConfig(true),
      ...value
    };
  }

  return {
    preceding: Boolean(value),
    current: Boolean(value)
  };
};

const normaliseCorporateRebateConfig = (value, isCrossYA) => {
  if (!isCrossYA) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const { preceding, current, ...singleValue } = value;
      const hasSingleValue = ["isRebate", "taxableRate", "cappedAmount"].some(
        (key) => Object.prototype.hasOwnProperty.call(singleValue, key)
      );

      return {
        ...defaultCorporateRebateConfig,
        ...(hasSingleValue ? singleValue : current || preceding || {})
      };
    }

    return {
      ...defaultCorporateRebateConfig
    };
  }

  const defaultConfig = buildDefaultCorporateRebate(true);
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return defaultConfig;
  }

  const fallback = {
    ...defaultCorporateRebateConfig,
    isRebate: value.isRebate ?? false,
    taxableRate: value.taxableRate ?? 0,
    cappedAmount: value.cappedAmount ?? 0
  };

  return {
    preceding: {
      ...fallback,
      ...(value.preceding || {})
    },
    current: {
      ...fallback,
      ...(value.current || {})
    }
  };
};

const shouldDeclareAgreementStatus = (row) =>
  row.toDeclareAgreement === true ||
  row.agreementStatus?.isAgreed != null ||
  Boolean(row.agreementStatus?.dateOfNOA) ||
  row.isAgreed != null ||
  Boolean(row.dateOfNOA);

const normaliseAgreementStatus = (row) =>
  shouldDeclareAgreementStatus(row)
    ? {
        dateOfNOA: row.agreementStatus?.dateOfNOA ?? row.dateOfNOA ?? "",
        isAgreed: row.agreementStatus?.isAgreed ?? row.isAgreed ?? false
      }
    : {
        dateOfNOA: null,
        isAgreed: null
      };

const MainTaxComputation = forwardRef(({ company }, ref) => {
  const handleSuccess = useSuccessDialog();
  const handleError = useErrorDialog();

  const { canView, canEdit } = useAccessControl(MAIN_COMPUTATION);

  const { getMainComputationData, updateMainComputation } =
    useContext(AuthContext);

  // Determine if this is an investment company
  const isInvestmentCompany = company?.companyType === "investment";
  const isCrossYA = company?.taxPeriodType === "cross_ya";

  // Select appropriate page sections based on company type
  const PageSections = isInvestmentCompany
    ? InvestmentPageSections
    : CommonPageSections;

  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [openNavList, setOpenNavList] = useState(false);
  const [priorYAUploadOpen, setPriorYAUploadOpen] = useState(false);
  const [currency, setCurrency] = useState("SGD");
  const [currencyRate, setCurrencyRate] = useState(1);
  const [masCurrencyRate, setMasCurrencyRate] = useState(null);
  const [currencyView, setCurrencyView] = useState("SGD");

  const [isStartup, setIsStartup] = useState(() =>
    buildDefaultStartupConfig(isCrossYA)
  );
  const [comments, setComments] = useState("");
  const [reviewNote, setReviewNote] = useState("");
  const [netProfitBeforeTax, setNetProfitBeforeTax] = useState(0);
  const [crossYAApportionmentContext, setCrossYAApportionmentContext] =
    useState(null);

  const [unutilisedCapitalAllowances, setUnutilisedCapitalAllowances] =
    useState([]);
  const [unutilisedLosses, setUnutilisedLosses] = useState([]);
  const [currentYearLosses, setCurrentYearLosses] = useState(null);
  const [taxExemptionAmount, setTaxExemptionAmount] = useState(0);
  const [corporateIncomeRebateAmount, setCorporateIncomeRebateAmount] =
    useState(0);
  const [unutilisedDonations, setUnutilisedDonations] = useState([]);
  const [corporateIncomeTaxRebate, setCorporateIncomeTaxRebate] = useState(() =>
    buildDefaultCorporateRebate(isCrossYA)
  );
  const [taxPreviouslyAssessed, setTaxPreviouslyAssessed] = useState([
    {
      date: "",
      amount: 0,
      historyAmount: []
    }
  ]);
  const [referenceData, setReferenceData] = useState({
    taxAdjustments: [],
    taxAdjustmentSeparateSourcedIncome: [],
    taxAdjustmentBalancingCharge: [],
    furtherDeductionS14n: [],
    furtherDeductionOther: [],
    currentYearCapitalAllowances: [],
    otherSourceIncome: [],
    currentYearDonations: [],
    currentYearInvestmentLosses: []
  });

  const [sectionTotals, setSectionTotals] = useState({
    TAX_ADJUSTMENTS: 0,
    TAX_ADJUSTMENT_SEPARATE_SOURCED_INCOME: 0,
    TAX_ADJUSTMENT_BALANCING_CHARGE: 0,
    FURTHER_DEDUCTION_S14N: 0,
    FURTHER_DEDUCTION_OTHER: 0,
    CURRENT_CA: 0,
    UN_CA: 0,
    UN_LOSS: 0,
    UN_DONATION: 0,
    CURRENT_DONATION: 0,
    OTHER_INCOME: 0,
    TAX_PREV_ASSESS: 0,
    CURRENT_YEAR_INVESTMENT_LOSSES: 0
  });
  const [computedSummary, setComputedSummary] = useState({
    adjustedProfitAfterTaxAdjustments: null,
    adjustedProfitAfterCapitalAllowances: null,
    adjustedProfitBeforeLosses: null,
    adjustedProfitAfterLosses: null,
    adjustedProfitAfterDonations: null,
    unutilisedCAcf: null,
    unutilisedLosscf: null,
    unutilisedDonationcf: null,
    currentYearLosses: null,
    chargebleIncomeBeforeExemptAmount: null,
    firstExemptAmount: null,
    secondExemptAmount: null,
    chargebleIncomeAfterExemptAmount: null,
    taxableIncome: null,
    postDeductions: null,
    netTaxPayable: null
  });

  const getActiveUnutilisedRows = (rows = []) => {
    if (!rows || rows.length === 0) return [];
    return rows.filter((row) => !row?.isForfeited);
  };

  const getUnabsorbedLossAmount = (row) =>
    Math.max(0, -getSignedUnutilisedLossAmount(row));

  const sortUnutilisedRowsByYear = (rows = []) => {
    const toYear = (row) => {
      let y = row?.yearOfAssessment;

      if (!y) {
        const str = row?.date;
        if (!str) return 0;

        if (
          typeof str === "number" &&
          Number.isInteger(str) &&
          str >= 1900 &&
          str <= 9999
        ) {
          return str;
        }

        const date = new Date(str);
        return !Number.isNaN(date.getTime()) && date.getFullYear() !== 1970
          ? date.getFullYear()
          : 0;
      }

      if (typeof y === "number") return y;

      const m = String(y).match(/(\d{4})/);
      return m ? Number(m[1]) : 0;
    };

    return [...rows].sort((a, b) => toYear(a) - toYear(b));
  };

  const normalizeUnutilisedRows = (rows = []) =>
    rows.map((row) =>
      isCrossYA
        ? defaultUnutilisedAmountToCurrentYA(row, true)
        : normalizeSingleYARow(row)
    );

  const normalizeTaxPreviouslyAssessedRows = (rows = []) =>
    isCrossYA
      ? apportionCrossYARows(rows, crossYAApportionmentContext)
      : normalizeSingleYATaxPreviouslyAssessedRows(rows);

  const buildUnabsorbedSummary = useCallback(() => {
    if (isInvestmentCompany) {
      return {
        caRows: [],
        lossRows: [],
        donationRows: []
      };
    }

    const sortYearCa = (row) => {
      const y = row?.yearOfAssessment;
      if (y == null || y === "") return 0;
      if (typeof y === "number") return y;
      const t = new Date(y).getFullYear();
      return Number.isNaN(t) ? 0 : t;
    };

    const sortYearFye = (row) => {
      const m = String(row?.yearOfAssessment ?? "").match(/FYE\s*(\d{4})/i);
      return m ? parseInt(m[1], 10) : 0;
    };

    const getLossFinancialYear = (item) => {
      return item.date && item.status !== "autoload"
        ? new Date(item.date).getFullYear()
        : item.yearOfAssessment - 1;
    };

    let caRows = [];
    let lossRows = [];
    let donationRows = [];

    let currentYearCa = MainComputationCalculator.calTaxAdjustments(
      referenceData["currentYearCapitalAllowances"]
    );

    const unutilisedCaAll = sortUnutilisedRowsByYear(
      unutilisedCapitalAllowances || []
    );
    let unutilisedLoss = sortUnutilisedRowsByYear(
      getActiveUnutilisedRows(unutilisedLosses)
    );
    let unutilisedDonation = sortUnutilisedRowsByYear(
      getActiveUnutilisedRows(unutilisedDonations)
    );
    let currentYearDonation = MainComputationCalculator.calTaxAdjustments(
      referenceData["currentYearDonations"]
    );
    const currentYearLossesForUnabsorbedLosses = (() => {
      const fallbackLosses = Math.abs(currentYearLosses || 0);
      if (!isCrossYA || !crossYAApportionmentContext) return fallbackLosses;

      return ["preceding_amount", "current_amount"].reduce((sum, amountKey) => {
        const { currentYearLosses: yaCurrentYearLosses } =
          buildYABaseComputation({
            amountKey,
            context: crossYAApportionmentContext,
            netProfitBeforeTax,
            referenceData
          });

        return sum + yaCurrentYearLosses;
      }, 0);
    })();
    const hasCurrentYearLossesForUnabsorbedLosses =
      currentYearLossesForUnabsorbedLosses > 0;

    let totalUtilisation = currentYearLosses
      ? MainComputationCalculator.justArraySum(
          referenceData["otherSourceIncome"]
        )
      : netProfitBeforeTax +
        MainComputationCalculator.justArraySum(
          referenceData["otherSourceIncome"]
        ) +
        MainComputationCalculator.calTaxAdjustments(
          referenceData["taxAdjustments"]
        ) +
        MainComputationCalculator.calTaxAdjustments(
          referenceData["taxAdjustmentSeparateSourcedIncome"]
        ) +
        MainComputationCalculator.calTaxAdjustments(
          referenceData["taxAdjustmentBalancingCharge"]
        ) +
        MainComputationCalculator.calTaxAdjustments(
          referenceData["furtherDeductionS14n"]
        ) +
        MainComputationCalculator.calTaxAdjustments(
          referenceData["furtherDeductionOther"]
        );

    if (totalUtilisation < 0) {
      totalUtilisation = 0;
    }

    if (currentYearCa > 0) totalUtilisation += currentYearCa;
    if (currentYearDonation > 0) totalUtilisation += currentYearDonation;

    let remaining = totalUtilisation;
    const allocateSingleYAAmount = (amount) => {
      const balance = Math.abs(parseFloat(amount) || 0);
      const utilised = Math.min(balance, Math.max(remaining, 0));
      remaining = Math.max(0, remaining - utilised);

      return {
        balance,
        utilised,
        balanceCf: balance - utilised
      };
    };
    const hasForfeitedLosses = (unutilisedLosses || []).some(
      (r) => r.isForfeited
    );
    const hasForfeitedDonations = (unutilisedDonations || []).some(
      (r) => r.isForfeited
    );

    if (isCrossYA && crossYAApportionmentContext) {
      const getYABaseComputation = (amountKey) =>
        buildYABaseComputation({
          amountKey,
          context: crossYAApportionmentContext,
          netProfitBeforeTax,
          referenceData
        });

      const precedingYABase = getYABaseComputation("preceding_amount");
      const currentYABase = getYABaseComputation("current_amount");

      let precedingRemaining = precedingYABase.utilisationAvailable;
      let currentRemaining = currentYABase.utilisationAvailable;

      const allocateCrossYARow = ({
        amount,
        currentAddition = 0,
        isForfeited = false,
        openingBalance = 0,
        precedingAddition = 0
      }) => {
        const precedingBalanceAvailable =
          Math.abs(parseFloat(openingBalance) || 0) +
          Math.abs(parseFloat(precedingAddition) || 0);

        let precedingUtilisedThisYear = 0;
        if (!isForfeited && precedingRemaining > 0) {
          precedingUtilisedThisYear = Math.min(
            precedingBalanceAvailable,
            precedingRemaining
          );
          precedingRemaining -= precedingUtilisedThisYear;
        }

        const precedingBalanceCf =
          precedingBalanceAvailable - precedingUtilisedThisYear;
        const currentBalanceAvailable =
          precedingBalanceCf + Math.abs(parseFloat(currentAddition) || 0);

        let currentUtilisedThisYear = 0;
        if (!isForfeited && currentRemaining > 0) {
          currentUtilisedThisYear = Math.min(
            currentBalanceAvailable,
            currentRemaining
          );
          currentRemaining -= currentUtilisedThisYear;
        }

        const currentBalanceCf =
          currentBalanceAvailable - currentUtilisedThisYear;

        return {
          amount,
          utilisedThisYear: precedingUtilisedThisYear + currentUtilisedThisYear,
          balanceCf: currentBalanceCf,
          precedingBalanceAvailable,
          precedingUtilisedThisYear,
          precedingBalanceCf,
          currentBalanceAvailable,
          currentUtilisedThisYear,
          currentBalanceCf
        };
      };

      const buildUnabsorbedRow = (baseRow, allocation) => ({
        ...baseRow,
        ...allocation
      });

      if (currentYearCa < 0 || unutilisedCaAll.length > 0) {
        caRows = unutilisedCaAll.map((item) => {
          const amount = getUnutilisedAmount(item, isCrossYA);
          return buildUnabsorbedRow(
            {
              yearOfAssessment: item.yearOfAssessment,
              exchangeRate: item.exchangeRate,
              isForfeited: item.isForfeited,
              toDeclareAgreement: item.toDeclareAgreement,
              agreementStatus: item.agreementStatus
            },
            allocateCrossYARow({
              amount,
              openingBalance: amount,
              isForfeited: item.isForfeited
            })
          );
        });

        if (currentYearCa < 0) {
          caRows = caRows.concat(
            buildUnabsorbedRow(
              {
                yearOfAssessment: company.yearOfAssessment,
                exchangeRate: currencyRate
              },
              allocateCrossYARow({
                amount: currentYearCa,
                precedingAddition: Math.min(precedingYABase.currentYearCa, 0),
                currentAddition: Math.min(currentYABase.currentYearCa, 0)
              })
            )
          );
        }

        caRows = [...caRows].sort((a, b) => sortYearCa(a) - sortYearCa(b));
      }

      const precedingYearLoss = precedingYABase.currentYearLosses;
      const currentYearLoss = currentYABase.currentYearLosses;
      const isPrecedingYACurrentYearLossRow = (row) =>
        row?.row_id === "auto-preceding-ya-current-year-loss" ||
        row?.name === "Current Year Losses - Preceding YA" ||
        row?.reference === "Current Year Losses - Preceding YA";
      const crossYAUnutilisedLossRows = unutilisedLoss.filter(
        (item) => !isPrecedingYACurrentYearLossRow(item)
      );
      const currentLossFinancialYear = company.yearOfAssessment - 1;
      const currentYALossRows = crossYAUnutilisedLossRows.filter(
        (item) => getLossFinancialYear(item) === currentLossFinancialYear
      );
      const priorYALossRows = crossYAUnutilisedLossRows.filter(
        (item) => getLossFinancialYear(item) !== currentLossFinancialYear
      );
      const netCurrentYearLoss = Math.max(
        0,
        precedingYearLoss + currentYearLoss -
          currentYALossRows.reduce(
            (sum, item) => sum + getSignedUnutilisedLossAmount(item),
            0
          )
      );

      if (
        priorYALossRows.length > 0 ||
        netCurrentYearLoss > 0 ||
        hasForfeitedLosses
      ) {
        lossRows = priorYALossRows.map((item) => {
          const ya = getLossFinancialYear(item) || item.yearOfAssessment;
          const amount = getUnabsorbedLossAmount(item);

          return buildUnabsorbedRow(
            {
              yearOfAssessment: `FYE ${ya}`,
              exchangeRate: item.exchangeRate,
              isForfeited: item.isForfeited,
              toDeclareAgreement: item.toDeclareAgreement,
              agreementStatus: item.agreementStatus
            },
            allocateCrossYARow({
              amount,
              openingBalance: amount,
              isForfeited: item.isForfeited
            })
          );
        });

        if (netCurrentYearLoss > 0) {
          const amount = netCurrentYearLoss;
          lossRows = lossRows.concat(
            buildUnabsorbedRow(
              {
                yearOfAssessment: `FYE ${company.yearOfAssessment - 1}`,
                exchangeRate: currencyRate
              },
              allocateCrossYARow({
                amount,
                currentAddition: amount
              })
            )
          );
        }

        lossRows = [...lossRows].sort(
          (a, b) => sortYearFye(a) - sortYearFye(b)
        );
      }

      if (
        unutilisedDonation.length > 0 ||
        currentYearDonation < 0 ||
        hasForfeitedDonations
      ) {
        donationRows = unutilisedDonation.map((item) => {
          const amount = getUnutilisedAmount(item, isCrossYA);
          return buildUnabsorbedRow(
            {
              yearOfAssessment: `FYE ${new Date(item.date).getFullYear()}`,
              exchangeRate: item.exchangeRate,
              isForfeited: item.isForfeited,
              toDeclareAgreement: item.toDeclareAgreement,
              agreementStatus: item.agreementStatus
            },
            allocateCrossYARow({
              amount,
              openingBalance: amount,
              isForfeited: item.isForfeited
            })
          );
        });

        if (currentYearDonation < 0) {
          donationRows = donationRows.concat(
            buildUnabsorbedRow(
              {
                yearOfAssessment: `FYE ${company.yearOfAssessment - 1}`,
                exchangeRate: currencyRate
              },
              allocateCrossYARow({
                amount: currentYearDonation,
                precedingAddition: Math.min(
                  precedingYABase.currentYearDonation,
                  0
                ),
                currentAddition: Math.min(currentYABase.currentYearDonation, 0)
              })
            )
          );
        }

        donationRows = [...donationRows].sort(
          (a, b) => sortYearFye(a) - sortYearFye(b)
        );
      }

      return {
        caRows,
        lossRows,
        donationRows
      };
    }

    // ========== CA ==========
    if (currentYearCa < 0 || unutilisedCaAll.length > 0) {
      let newItems = [];
      for (const item of unutilisedCaAll) {
        const amount = getUnutilisedAmount(item, isCrossYA);
        if (item.isForfeited) {
          const balance = Math.abs(amount);
          newItems.push({
            yearOfAssessment: item.yearOfAssessment,
            amount,
            utilisedThisYear: 0,
            balanceCf: balance,
            exchangeRate: item.exchangeRate,
            isForfeited: true,
            toDeclareAgreement: item.toDeclareAgreement,
            agreementStatus: item.agreementStatus
          });
          continue;
        }

        const { utilised, balanceCf } = allocateSingleYAAmount(amount);

        newItems.push({
          yearOfAssessment: item.yearOfAssessment,
          amount,
          utilisedThisYear: utilised,
          balanceCf,
          exchangeRate: item.exchangeRate,
          isForfeited: false,
          toDeclareAgreement: item.toDeclareAgreement,
          agreementStatus: item.agreementStatus
        });
      }

      if (currentYearCa < 0) {
        const { utilised, balanceCf } = allocateSingleYAAmount(currentYearCa);
        newItems = newItems.concat({
          yearOfAssessment: company.yearOfAssessment,
          amount: currentYearCa,
          utilisedThisYear: utilised,
          balanceCf,
          exchangeRate: currencyRate
        });
      }

      caRows = [...newItems].sort((a, b) => sortYearCa(a) - sortYearCa(b));
    }

    // ========== Loss ==========
    if (
      unutilisedLoss.length > 0 ||
      hasCurrentYearLossesForUnabsorbedLosses ||
      hasForfeitedLosses
    ) {
      const currentLossFinancialYear = company.yearOfAssessment - 1;
      const prevUL = unutilisedLoss.filter(
        (item) => getLossFinancialYear(item) !== currentLossFinancialYear
      );
      const currentUL = unutilisedLoss.filter(
        (item) => getLossFinancialYear(item) === currentLossFinancialYear
      );

      let newItems = prevUL.map((item) => {
        const ya = getLossFinancialYear(item) || item.yearOfAssessment;
        const amount = getUnabsorbedLossAmount(item);

        const { utilised, balanceCf } = allocateSingleYAAmount(amount);

        return {
          yearOfAssessment: `FYE ${ya}`,
          amount: amount,
          utilisedThisYear: utilised,
          balanceCf,
          exchangeRate: item.exchangeRate,
          toDeclareAgreement: item.toDeclareAgreement,
          agreementStatus: item.agreementStatus
        };
      });

      if (currentUL.length > 0 || hasCurrentYearLossesForUnabsorbedLosses) {
        const currentAmount = Math.max(
          0,
          currentYearLossesForUnabsorbedLosses -
            currentUL.reduce(
              (sum, item) => sum + getSignedUnutilisedLossAmount(item),
              0
            )
        );

        if (currentAmount > 0) {
          const { utilised, balanceCf } = allocateSingleYAAmount(currentAmount);
          newItems = newItems.concat({
            yearOfAssessment: `FYE ${currentLossFinancialYear}`,
            amount: currentAmount,
            currentYearLossesAmount: currentYearLossesForUnabsorbedLosses,
            utilisedThisYear: utilised,
            balanceCf,
            exchangeRate: currencyRate
          });
        }
      }

      lossRows = newItems;

      (unutilisedLosses || [])
        .filter((r) => r.isForfeited)
        .forEach((item) => {
          const ya = getLossFinancialYear(item) || item.yearOfAssessment;
          const amount = getUnabsorbedLossAmount(item);
          const balance = Math.abs(amount);
          lossRows.push({
            yearOfAssessment: `FYE ${ya}`,
            amount,
            utilisedThisYear: 0,
            balanceCf: balance,
            exchangeRate: item.exchangeRate,
            isForfeited: true,
            toDeclareAgreement: item.toDeclareAgreement,
            agreementStatus: item.agreementStatus
          });
        });

      lossRows = [...lossRows].sort((a, b) => sortYearFye(a) - sortYearFye(b));
    }

    // ========== Donation ==========
    if (
      unutilisedDonation.length > 0 ||
      currentYearDonation < 0 ||
      hasForfeitedDonations
    ) {
      let newItems = unutilisedDonation.map((item) => {
        const amount = getUnutilisedAmount(item, isCrossYA);
        const { utilised, balanceCf } = allocateSingleYAAmount(amount);

        return {
          yearOfAssessment: `FYE ${new Date(item.date).getFullYear()}`,
          amount,
          utilisedThisYear: utilised,
          balanceCf,
          exchangeRate: item.exchangeRate,
          toDeclareAgreement: item.toDeclareAgreement,
          agreementStatus: item.agreementStatus
        };
      });

      if (currentYearDonation < 0) {
        const { utilised, balanceCf } =
          allocateSingleYAAmount(currentYearDonation);
        newItems = newItems.concat({
          yearOfAssessment: `FYE ${company.yearOfAssessment - 1}`,
          amount: currentYearDonation,
          utilisedThisYear: utilised,
          balanceCf,
          exchangeRate: currencyRate
        });
      }

      donationRows = newItems;

      (unutilisedDonations || [])
        .filter((r) => r.isForfeited)
        .forEach((item) => {
          const amount = getUnutilisedAmount(item, isCrossYA);
          const balance = Math.abs(amount);
          donationRows.push({
            yearOfAssessment: `FYE ${new Date(item.date).getFullYear()}`,
            amount,
            utilisedThisYear: 0,
            balanceCf: balance,
            exchangeRate: item.exchangeRate,
            isForfeited: true,
            toDeclareAgreement: item.toDeclareAgreement,
            agreementStatus: item.agreementStatus
          });
        });

      donationRows = [...donationRows].sort(
        (a, b) => sortYearFye(a) - sortYearFye(b)
      );
    }

    return {
      caRows,
      lossRows,
      donationRows
    };
  }, [
    isInvestmentCompany,
    referenceData,
    unutilisedCapitalAllowances,
    unutilisedLosses,
    unutilisedDonations,
    currentYearLosses,
    isCrossYA,
    crossYAApportionmentContext,
    netProfitBeforeTax,
    company.yearOfAssessment,
    currencyRate
  ]);

  const getYearRebate = useCallback((year) => {
    const numericYear = Number(year);
    return (
      defaultYearRebates.find((r) => r.year === numericYear) || {
        year: numericYear || year || null,
        amount: 0
      }
    );
  }, []);
  const currentYearRebate = useMemo(
    () => getYearRebate(company.yearOfAssessment),
    [company.yearOfAssessment, getYearRebate]
  );
  const rebateByYA = useMemo(
    () => ({
      preceding: getYearRebate(Number(company.yearOfAssessment) - 1),
      current: currentYearRebate
    }),
    [company.yearOfAssessment, currentYearRebate, getYearRebate]
  );
  const activeCurrentYearRebate = isCrossYA ? rebateByYA : currentYearRebate;

  const [isDirty, setIsDirty] = useState(false);
  useImperativeHandle(ref, () => ({
    isDirty: () => isDirty,
    clearDirtyFlag: () => {
      setIsDirty(false);
    },
    getUnsavedData: () => {
      const normalizedReferenceData = normalizeReferenceDataForTaxPeriod(
        referenceData,
        isCrossYA,
        crossYAApportionmentContext
      );
      const sortedUnutilisedCapitalAllowances = isInvestmentCompany
        ? []
        : normalizeUnutilisedRows(
            sortUnutilisedRowsByYear(unutilisedCapitalAllowances)
          );
      const sortedUnutilisedLosses = isInvestmentCompany
        ? []
        : normalizeUnutilisedRows(sortUnutilisedRowsByYear(unutilisedLosses));
      const sortedUnutilisedDonations = isInvestmentCompany
        ? []
        : normalizeUnutilisedRows(
            sortUnutilisedRowsByYear(unutilisedDonations)
          );
      const normalizedTaxPreviouslyAssessed =
        normalizeTaxPreviouslyAssessedRows(taxPreviouslyAssessed);

      return {
        tc_id: company.tc_id,
        // For investment companies, reuse "Tax Adjustments" key (UI shows "Net Investment Income")
        "Tax Adjustments": normalizedReferenceData["taxAdjustments"] || [],
        [TAX_ADJUSTMENT_SEPARATE_SOURCED_INCOME]: isInvestmentCompany
          ? []
          : normalizedReferenceData["taxAdjustmentSeparateSourcedIncome"] || [],
        [TAX_ADJUSTMENT_BALANCING_CHARGE]: isInvestmentCompany
          ? []
          : normalizedReferenceData["taxAdjustmentBalancingCharge"] || [],
        [FURTHER_DEDUCTION_S14N]: isInvestmentCompany
          ? []
          : normalizedReferenceData["furtherDeductionS14n"] || [],
        [FURTHER_DEDUCTION_OTHER]: isInvestmentCompany
          ? []
          : normalizedReferenceData["furtherDeductionOther"] || [],
        "Current Year Capital Allowances":
          normalizedReferenceData["currentYearCapitalAllowances"] || [],
        "Unutilised Capital Allowances": isInvestmentCompany
          ? []
          : sortedUnutilisedCapitalAllowances,
        "Unutilised Losses": isInvestmentCompany ? [] : sortedUnutilisedLosses,
        "Unutilised Donations": isInvestmentCompany
          ? []
          : sortedUnutilisedDonations,
        "Other Source Income": isInvestmentCompany
          ? []
          : normalizedReferenceData["otherSourceIncome"] || [],
        "Current Year Donations":
          normalizedReferenceData["currentYearDonations"] || [],
        "Current Year Investment Losses":
          normalizedReferenceData["currentYearInvestmentLosses"] || [],
        "Partial Tax Exemption / Full Tax Exemption": taxExemptionAmount,
        "Corporate Income Tax Rebate": corporateIncomeTaxRebate,
        "Tax Previously Assessed": normalizedTaxPreviouslyAssessed,
        currentYearLosses: currentYearLosses || 0,
        adjustedProfitAfterTaxAdjustments:
          computedSummary.adjustedProfitAfterTaxAdjustments || 0,
        unutilisedCAcf: computedSummary.unutilisedCAcf || 0,
        unutilisedLosscf: computedSummary.unutilisedLosscf || 0,
        unutilisedDonationcf: computedSummary.unutilisedDonationcf || 0,
        isStartup: isStartup,
        comments: comments,
        reviewNote: reviewNote,
        doc: JSON.stringify({
          company,
          currency,
          currencyRate,
          netProfitBeforeTax,
          referenceData: normalizedReferenceData,
          unutilisedCapitalAllowances: sortedUnutilisedCapitalAllowances,
          unutilisedLosses: sortedUnutilisedLosses,
          currentYearLosses,
          unutilisedDonations: sortedUnutilisedDonations,
          corporateIncomeTaxRebate,
          currentYearRebate: activeCurrentYearRebate,
          isStartup,
          taxPreviouslyAssessed: normalizedTaxPreviouslyAssessed,
          computedSummary,
          unabsorbedSummary: buildUnabsorbedSummary(),
          crossYAApportionmentContext,
          comments,
          reviewNote
        })
      };
    }
  }));
  const recordChangeHappened = () => setIsDirty(true);
  const recordSaved = () => setIsDirty(false);

  //Initial Load
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);

        let data = await getMainComputationData(company.tc_id);
        if (!data) {
          throw "Unexpected error occurs when getting your data. Please try again later.";
        }

        const nextCrossYAApportionmentContext = getCrossYAApportionmentContext(
          {
            ...company,
            basisPeriods: data.basisPeriods ?? company.basisPeriods,
            isCustomizedBasisPeriod:
              data.isCustomizedBasisPeriod ?? company.isCustomizedBasisPeriod
          },
          isCrossYA
        );
        setCrossYAApportionmentContext(nextCrossYAApportionmentContext);

        if (company.companyType !== "investment")
          setNetProfitBeforeTax(data.netProfitBeforeTax || 0);
        else setNetProfitBeforeTax(0);

        setUnutilisedLosses(
          (data[UN_LOSS] || [])
            .map((row) => ({
              ...row,
              exchangeRate: row.exchangeRate ?? data.currency_rate,
              isForfeited: row.isForfeited ?? false,
              toDeclareAgreement: shouldDeclareAgreementStatus(row),
              agreementStatus: normaliseAgreementStatus(row)
            }))
            .map((row) =>
              isCrossYA
                ? defaultUnutilisedAmountToCurrentYA(row, true)
                : normalizeSingleYARow(row)
            )
        );

        setUnutilisedDonations(
          (data[UN_DONATION] || [])
            .map((row) => ({
              ...row,
              exchangeRate: row.exchangeRate ?? data.currency_rate,
              isForfeited: row.isForfeited ?? false,
              toDeclareAgreement: shouldDeclareAgreementStatus(row),
              agreementStatus: normaliseAgreementStatus(row)
            }))
            .map((row) =>
              isCrossYA
                ? defaultUnutilisedAmountToCurrentYA(row, true)
                : normalizeSingleYARow(row)
            )
        );

        setUnutilisedCapitalAllowances(
          (data[UN_CA] || [])
            .map((row) => ({
              ...row,
              exchangeRate: row.exchangeRate ?? data.currency_rate,
              isForfeited: row.isForfeited ?? false,
              toDeclareAgreement: shouldDeclareAgreementStatus(row),
              agreementStatus: normaliseAgreementStatus(row)
            }))
            .map((row) =>
              isCrossYA
                ? defaultUnutilisedAmountToCurrentYA(row, true)
                : normalizeSingleYARow(row)
            )
        );

        setCorporateIncomeTaxRebate(
          normaliseCorporateRebateConfig(data[COR_TAX_REBATE], isCrossYA)
        );
        setTaxPreviouslyAssessed(
          isCrossYA
            ? normalizeRowsForTaxPeriod(
                data[TAX_PREV_ASSESS] || [
                  { date: "", amount: 0, historyAmount: [] }
                ],
                true,
                nextCrossYAApportionmentContext
              )
            : normalizeSingleYATaxPreviouslyAssessedRows(
                data[TAX_PREV_ASSESS] || [
                  { date: "", amount: 0, historyAmount: [] }
                ]
              )
        );
        setReferenceData(
          normalizeReferenceDataForTaxPeriod(
            {
              taxAdjustments: data[TAX_ADJUSTMENTS] || [],
              taxAdjustmentSeparateSourcedIncome:
                data[TAX_ADJUSTMENT_SEPARATE_SOURCED_INCOME] || [],
              taxAdjustmentBalancingCharge:
                data[TAX_ADJUSTMENT_BALANCING_CHARGE] || [],
              furtherDeductionS14n: data[FURTHER_DEDUCTION_S14N] || [],
              furtherDeductionOther: data[FURTHER_DEDUCTION_OTHER] || [],
              currentYearCapitalAllowances: data[CURRENT_CA] || [],
              otherSourceIncome: data[OTHER_INCOME] || [],
              currentYearDonations: data[CURRENT_DONATION] || [],
              currentYearInvestmentLosses:
                data[CURRENT_YEAR_INVESTMENT_LOSSES] || []
            },
            isCrossYA,
            nextCrossYAApportionmentContext
          )
        );

        setCurrency(data.currency);
        setCurrencyView(data.currency);
        if (data.masExchangeRate && !data.masExchangeRate.error)
          setMasCurrencyRate(data.masExchangeRate);
        setCurrencyRate(data.manual_currency_rate || data.currency_rate);
        setIsStartup(normaliseStartupConfig(data.isStartup, isCrossYA));
        setComments(data.comments || "");
        setReviewNote(data.reviewNote || "");
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [
    company.tc_id,
    company.uen,
    company.id,
    company.companyType,
    company.taxPeriodType,
    company.isCustomizedBasisPeriod,
    company.basisPeriods,
    company.financialYearStart,
    company.financialYearEnd,
    company.yearOfAssessment,
    isCrossYA
  ]);

  const referenceSectionTotals = useMemo(() => {
    return {
      TAX_ADJUSTMENTS: MainComputationCalculator.calTaxAdjustments(
        referenceData.taxAdjustments
      ),
      TAX_ADJUSTMENT_SEPARATE_SOURCED_INCOME:
        MainComputationCalculator.calTaxAdjustments(
          referenceData.taxAdjustmentSeparateSourcedIncome
        ),
      TAX_ADJUSTMENT_BALANCING_CHARGE:
        MainComputationCalculator.calTaxAdjustments(
          referenceData.taxAdjustmentBalancingCharge
        ),
      FURTHER_DEDUCTION_S14N: MainComputationCalculator.calTaxAdjustments(
        referenceData.furtherDeductionS14n
      ),
      FURTHER_DEDUCTION_OTHER: MainComputationCalculator.calTaxAdjustments(
        referenceData.furtherDeductionOther
      ),
      CURRENT_CA: MainComputationCalculator.calTaxAdjustments(
        referenceData.currentYearCapitalAllowances
      ),
      CURRENT_DONATION: MainComputationCalculator.calTaxAdjustments(
        referenceData.currentYearDonations
      ),
      CURRENT_YEAR_INVESTMENT_LOSSES:
        MainComputationCalculator.calTaxAdjustments(
          referenceData.currentYearInvestmentLosses
        ),
      OTHER_INCOME: MainComputationCalculator.justArraySum(
        referenceData.otherSourceIncome
      )
    };
  }, [
    referenceData.taxAdjustments,
    referenceData.taxAdjustmentSeparateSourcedIncome,
    referenceData.taxAdjustmentBalancingCharge,
    referenceData.furtherDeductionS14n,
    referenceData.furtherDeductionOther,
    referenceData.currentYearCapitalAllowances,
    referenceData.currentYearDonations,
    referenceData.currentYearInvestmentLosses,
    referenceData.otherSourceIncome
  ]);

  useEffect(() => {
    const activeUnutilisedCapitalAllowances = getActiveUnutilisedRows(
      unutilisedCapitalAllowances
    );
    const activeUnutilisedLosses = getActiveUnutilisedRows(unutilisedLosses);
    const activeUnutilisedDonations =
      getActiveUnutilisedRows(unutilisedDonations);

    const UN_CA = -activeUnutilisedCapitalAllowances.reduce(
      (sum, row) => sum + Math.abs(getUnutilisedAmount(row, isCrossYA)),
      0
    );
    const UN_LOSS = activeUnutilisedLosses.reduce(
      (sum, row) => sum + getSignedUnutilisedLossAmount(row),
      0
    );
    const UN_DONATION = -activeUnutilisedDonations.reduce(
      (sum, row) => sum + Math.abs(getUnutilisedAmount(row, isCrossYA)),
      0
    );
    const TAX_PREV_ASSESS = calculateTaxPreviouslyAssessedTotal(
      taxPreviouslyAssessed,
      (row) => getTaxPreviouslyAssessedAmount(row, isCrossYA)
    );

    setSectionTotals({
      ...referenceSectionTotals,
      UN_CA,
      UN_LOSS,
      UN_DONATION,
      TAX_PREV_ASSESS
    });
  }, [
    referenceSectionTotals,
    unutilisedCapitalAllowances,
    unutilisedLosses,
    unutilisedDonations,
    corporateIncomeTaxRebate,
    taxPreviouslyAssessed,
    isInvestmentCompany,
    isCrossYA
  ]);

  const debouncedRecalculateSummary = useMemo(() => {
    return debounce((totals) => {
      const {
        TAX_ADJUSTMENTS,
        TAX_ADJUSTMENT_SEPARATE_SOURCED_INCOME,
        TAX_ADJUSTMENT_BALANCING_CHARGE,
        FURTHER_DEDUCTION_S14N,
        FURTHER_DEDUCTION_OTHER,
        CURRENT_CA,
        UN_CA,
        OTHER_INCOME,
        UN_LOSS,
        UN_DONATION,
        CURRENT_DONATION,
        CURRENT_YEAR_INVESTMENT_LOSSES
      } = totals;

      if (isCrossYA && crossYAApportionmentContext) {
        const commonSummaryProps = {
          company,
          context: crossYAApportionmentContext,
          corporateIncomeTaxRebate,
          currency: currencyView,
          currencyRate,
          currentYearRebate: activeCurrentYearRebate,
          isInvestmentCompany,
          isStartup,
          netProfitBeforeTax,
          referenceData,
          taxPreviouslyAssessed,
          unutilisedCapitalAllowances,
          unutilisedDonations,
          unutilisedLosses
        };
        const precedingSummary = buildYASummary({
          ...commonSummaryProps,
          amountKey: "preceding_amount",
          isCurrentYA: false
        });
        const currentSummary = buildYASummary({
          ...commonSummaryProps,
          amountKey: "current_amount",
          isCurrentYA: true,
          yearLossesBroughtForward: precedingSummary.currentYearLosses
        });
        const totalTaxExemption =
          Math.abs(precedingSummary.totalExemptedAmount || 0) +
          Math.abs(currentSummary.totalExemptedAmount || 0);
        const totalCorporateRebate =
          Number(precedingSummary.corporateRebate || 0) +
          Number(currentSummary.corporateRebate || 0);
        const totalCurrentYearLosses =
          Math.abs(precedingSummary.currentYearLosses || 0) +
          Math.abs(currentSummary.currentYearLosses || 0);

        setTaxExemptionAmount(totalTaxExemption);
        setCorporateIncomeRebateAmount(totalCorporateRebate);
        setCurrentYearLosses(totalCurrentYearLosses || null);
        setComputedSummary({
          preceding: precedingSummary,
          current: currentSummary,
          adjustedProfitAfterTaxAdjustments:
            Number(precedingSummary.steps?.[0]?.value || 0) +
            Number(currentSummary.steps?.[0]?.value || 0),
          adjustedProfitAfterCapitalAllowances:
            Number(precedingSummary.steps?.[1]?.value || 0) +
            Number(currentSummary.steps?.[1]?.value || 0),
          adjustedProfitAfterLosses:
            Number(precedingSummary.steps?.[2]?.value || 0) +
            Number(currentSummary.steps?.[2]?.value || 0),
          adjustedProfitAfterDonations:
            Number(precedingSummary.steps?.[3]?.value || 0) +
            Number(currentSummary.steps?.[3]?.value || 0),
          unutilisedCAcf:
            Number(precedingSummary.unutilisedCAcf || 0) +
            Number(currentSummary.unutilisedCAcf || 0),
          unutilisedLosscf:
            Number(precedingSummary.unutilisedLosscf || 0) +
            Number(currentSummary.unutilisedLosscf || 0),
          unutilisedDonationcf:
            Number(precedingSummary.unutilisedDonationcf || 0) +
            Number(currentSummary.unutilisedDonationcf || 0),
          currentYearLosses: totalCurrentYearLosses || null,
          chargebleIncomeBeforeExemptAmount:
            Number(precedingSummary.chargebleIncomeBeforeExemptAmount || 0) +
            Number(currentSummary.chargebleIncomeBeforeExemptAmount || 0),
          firstExemptAmount:
            Number(precedingSummary.firstExemptAmount || 0) +
            Number(currentSummary.firstExemptAmount || 0),
          secondExemptAmount:
            Number(precedingSummary.secondExemptAmount || 0) +
            Number(currentSummary.secondExemptAmount || 0),
          chargebleIncomeAfterExemptAmount:
            Number(precedingSummary.chargebleIncomeAfterExemptAmount || 0) +
            Number(currentSummary.chargebleIncomeAfterExemptAmount || 0),
          taxableIncome:
            Number(precedingSummary.taxableIncome || 0) +
            Number(currentSummary.taxableIncome || 0),
          postDeductions:
            Number(precedingSummary.postDeductions || 0) +
            Number(currentSummary.postDeductions || 0),
          netTaxPayable:
            Number(precedingSummary.netTaxPayable || 0) +
            Number(currentSummary.netTaxPayable || 0)
        });
        setSummaryLoading(false);
        return;
      }

      let currentYearLosses = null;
      let unutilisedCAcf = null;
      let unutilisedLosscf = null;
      let unutilisedDonationcf = null;

      const adjustedProfitAfterTaxAdjustments =
        (isInvestmentCompany ? 0 : netProfitBeforeTax) +
        TAX_ADJUSTMENTS +
        TAX_ADJUSTMENT_SEPARATE_SOURCED_INCOME +
        TAX_ADJUSTMENT_BALANCING_CHARGE +
        FURTHER_DEDUCTION_S14N +
        FURTHER_DEDUCTION_OTHER;

      const hasCurrentYearLosses = adjustedProfitAfterTaxAdjustments < 0;
      let adjustedProfitAfterCapitalAllowances = 0;
      let adjustedProfitBeforeLosses = null;
      let adjustedProfitAfterLosses = 0;
      let adjustedProfitAfterDonations = 0;

      if (hasCurrentYearLosses) {
        currentYearLosses = Math.abs(adjustedProfitAfterTaxAdjustments);
        adjustedProfitAfterCapitalAllowances =
          OTHER_INCOME + CURRENT_CA - Math.abs(UN_CA);

        if (adjustedProfitAfterCapitalAllowances < 0)
          unutilisedCAcf = adjustedProfitAfterCapitalAllowances;

        adjustedProfitAfterLosses =
          Math.max(0, adjustedProfitAfterCapitalAllowances) +
          UN_LOSS -
          currentYearLosses;
        if (adjustedProfitAfterLosses < 0)
          unutilisedLosscf = adjustedProfitAfterLosses;

        adjustedProfitAfterDonations = CURRENT_DONATION - Math.abs(UN_DONATION);
        if (adjustedProfitAfterLosses >= 0)
          adjustedProfitAfterDonations += adjustedProfitAfterLosses;
        if (adjustedProfitAfterDonations < 0)
          unutilisedDonationcf = adjustedProfitAfterDonations;
      } else {
        currentYearLosses = null;
        adjustedProfitAfterCapitalAllowances =
          adjustedProfitAfterTaxAdjustments + CURRENT_CA - Math.abs(UN_CA);
        adjustedProfitBeforeLosses =
          adjustedProfitAfterCapitalAllowances + OTHER_INCOME;

        adjustedProfitAfterLosses = UN_LOSS;
        if (adjustedProfitBeforeLosses < 0)
          unutilisedCAcf = adjustedProfitBeforeLosses;
        else
          adjustedProfitAfterLosses =
            adjustedProfitBeforeLosses + adjustedProfitAfterLosses;

        adjustedProfitAfterDonations = CURRENT_DONATION - Math.abs(UN_DONATION);
        if (adjustedProfitAfterLosses < 0) {
          unutilisedLosscf = adjustedProfitAfterLosses;
          if (adjustedProfitAfterDonations < 0)
            unutilisedDonationcf = adjustedProfitAfterDonations;
        } else
          adjustedProfitAfterDonations =
            adjustedProfitAfterLosses + adjustedProfitAfterDonations;

        if (adjustedProfitAfterDonations < 0) {
          unutilisedDonationcf = adjustedProfitAfterDonations;
        }
      }

      let chargebleIncomeBeforeExemptAmount = 0;
      if (!unutilisedCAcf && !unutilisedLosscf && !unutilisedDonationcf) {
        chargebleIncomeBeforeExemptAmount = adjustedProfitAfterDonations;
      }

      if (isInvestmentCompany) {
        chargebleIncomeBeforeExemptAmount =
          TAX_ADJUSTMENTS +
          CURRENT_CA +
          CURRENT_DONATION +
          CURRENT_YEAR_INVESTMENT_LOSSES;
      }

      const originalCurrency = String(
        currencyView ?? company?.currency ?? "SGD"
      ).toUpperCase();
      const isSgdCurrency = originalCurrency === "SGD";

      if (!isSgdCurrency && !isInvestmentCompany) {
        const sections = buildIrasMainCompSections({
          netProfitBeforeTax,
          referenceData,
          unutilisedCapitalAllowances,
          unutilisedDonations,
          unutilisedLosses
        });
        const sgdCalculation = calculateIrasSgdSectionResults({
          currencyRate: currencyRate ?? company?.currencyRate ?? 1,
          sections: getRenderableIrasMainCompSections(sections),
          unutilisedCapitalAllowances,
          unutilisedDonations,
          unutilisedLosses
        });
        chargebleIncomeBeforeExemptAmount =
          sgdCalculation.chargeableIncomeBeforeExemptAmount ?? 0;
      } else if (!isSgdCurrency && chargebleIncomeBeforeExemptAmount > 0) {
        chargebleIncomeBeforeExemptAmount =
          MainComputationCalculator.convertChargeableIncomeToSgd(
            chargebleIncomeBeforeExemptAmount,
            currencyRate,
            [
              ...unutilisedCapitalAllowances,
              ...unutilisedLosses,
              ...unutilisedDonations
            ]
          );
      }

      chargebleIncomeBeforeExemptAmount = Math.max(
        0,
        Math.round(chargebleIncomeBeforeExemptAmount)
      );

      const resolvedTaxSummary = resolveIrasChargeableIncomeSummary({
        chargeableIncomeBeforeExemptAmount: chargebleIncomeBeforeExemptAmount,
        corporateIncomeTaxRebate,
        currentYearRebate: activeCurrentYearRebate,
        isStartup,
        taxPreviouslyAssessed
      });
      const totalCorporateIncomeTaxRebate =
        -resolvedTaxSummary.rebate.netRebate;

      setTaxExemptionAmount(
        resolvedTaxSummary.firstExemptAmount +
          resolvedTaxSummary.secondExemptAmount
      );
      setCorporateIncomeRebateAmount(totalCorporateIncomeTaxRebate);

      setCurrentYearLosses(currentYearLosses);
      setComputedSummary({
        adjustedProfitAfterTaxAdjustments,
        adjustedProfitAfterCapitalAllowances,
        adjustedProfitBeforeLosses,
        adjustedProfitAfterLosses,
        adjustedProfitAfterDonations,
        unutilisedCAcf,
        unutilisedLosscf,
        unutilisedDonationcf,
        currentYearLosses,
        chargebleIncomeBeforeExemptAmount:
          resolvedTaxSummary.chargeableIncomeBeforeExemptAmount,
        firstExemptAmount: resolvedTaxSummary.firstExemptAmount,
        secondExemptAmount: resolvedTaxSummary.secondExemptAmount,
        chargebleIncomeAfterExemptAmount:
          resolvedTaxSummary.chargeableIncomeAfterExemptAmount,
        taxableIncome: resolvedTaxSummary.taxableIncome,
        postDeductions: resolvedTaxSummary.postDeductions,
        netTaxPayable: resolvedTaxSummary.netTaxPayable
      });

      setSummaryLoading(false);
    }, 500);
  }, [
    netProfitBeforeTax,
    corporateIncomeTaxRebate,
    activeCurrentYearRebate,
    crossYAApportionmentContext,
    isCrossYA,
    isStartup,
    isInvestmentCompany,
    company,
    currencyView,
    currencyRate,
    referenceData,
    taxPreviouslyAssessed,
    unutilisedCapitalAllowances,
    unutilisedLosses,
    unutilisedDonations
  ]);

  useEffect(() => {
    return () => {
      debouncedRecalculateSummary.cancel();
    };
  }, [debouncedRecalculateSummary]);

  useEffect(() => {
    setSummaryLoading(true);
    debouncedRecalculateSummary(sectionTotals);
  }, [
    sectionTotals,
    corporateIncomeTaxRebate,
    isStartup,
    currencyView,
    currencyRate,
    unutilisedCapitalAllowances,
    unutilisedLosses,
    unutilisedDonations,
    debouncedRecalculateSummary
  ]);

  // Check view access first - if user can't view, show access denied
  if (!canView) {
    return (
      <AccessDenied
        title='Access Denied'
        message="You don't have permission to view the Main Tax Computation. Please contact your administrator if you believe this is an error."
        icon={<LockOutlinedIcon sx={{ fontSize: 48, color: "grey.400" }} />}
      />
    );
  }

  if (loading) return <ComponentLoading />;

  const getSectionTotal = (id) => {
    return sectionTotals[id] ?? 0;
  };

  const getUnutilisedDatasetById = (id) => {
    switch (id) {
      case UN_CA:
        return unutilisedCapitalAllowances;
      case UN_LOSS:
        return unutilisedLosses;
      case UN_DONATION:
        return unutilisedDonations;
      default:
        return [];
    }
  };

  const getDataKey = (id) => {
    const dataKeyMap = {
      [TAX_ADJUSTMENTS]: "taxAdjustments",
      [TAX_ADJUSTMENT_SEPARATE_SOURCED_INCOME]:
        "taxAdjustmentSeparateSourcedIncome",
      [TAX_ADJUSTMENT_BALANCING_CHARGE]: "taxAdjustmentBalancingCharge",
      [FURTHER_DEDUCTION_S14N]: "furtherDeductionS14n",
      [FURTHER_DEDUCTION_OTHER]: "furtherDeductionOther",
      [CURRENT_CA]: "currentYearCapitalAllowances",
      [UN_CA]: "unutilisedCapitalAllowances",
      [UN_LOSS]: "unutilisedLosses",
      [UN_DONATION]: "unutilisedDonations",
      [CURRENT_DONATION]: "currentYearDonations",
      [CURRENT_YEAR_INVESTMENT_LOSSES]: "currentYearInvestmentLosses",
      [OTHER_INCOME]: "otherSourceIncome",
      [TAX_EXEMP]: "taxExemption",
      [COR_TAX_REBATE]: "corporateTaxRebate",
      [TAX_PREV_ASSESS]: "taxPreviouslyAssessed"
    };
    return dataKeyMap[id] ?? "";
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const normalizedReferenceData = normalizeReferenceDataForTaxPeriod(
        referenceData,
        isCrossYA,
        crossYAApportionmentContext
      );
      const sortedUnutilisedCapitalAllowances = isInvestmentCompany
        ? []
        : normalizeUnutilisedRows(
            sortUnutilisedRowsByYear(unutilisedCapitalAllowances)
          );
      const sortedUnutilisedLosses = isInvestmentCompany
        ? []
        : normalizeUnutilisedRows(sortUnutilisedRowsByYear(unutilisedLosses));
      const sortedUnutilisedDonations = isInvestmentCompany
        ? []
        : normalizeUnutilisedRows(
            sortUnutilisedRowsByYear(unutilisedDonations)
          );
      const normalizedTaxPreviouslyAssessed =
        normalizeTaxPreviouslyAssessedRows(taxPreviouslyAssessed);

      setUnutilisedCapitalAllowances(sortedUnutilisedCapitalAllowances);
      setUnutilisedLosses(sortedUnutilisedLosses);
      setUnutilisedDonations(sortedUnutilisedDonations);
      setTaxPreviouslyAssessed(normalizedTaxPreviouslyAssessed);
      setReferenceData(normalizedReferenceData);

      const mainComputationSaveData = {
        tc_id: company.tc_id,
        // For investment companies, save as "Tax Adjustments" (reused key) for easier maintenance
        // The UI label shows "Net Investment Income" but data key remains "Tax Adjustments"
        "Tax Adjustments": normalizedReferenceData["taxAdjustments"] || [],
        [TAX_ADJUSTMENT_SEPARATE_SOURCED_INCOME]: isInvestmentCompany
          ? []
          : normalizedReferenceData["taxAdjustmentSeparateSourcedIncome"] || [],
        [TAX_ADJUSTMENT_BALANCING_CHARGE]: isInvestmentCompany
          ? []
          : normalizedReferenceData["taxAdjustmentBalancingCharge"] || [],
        [FURTHER_DEDUCTION_S14N]: isInvestmentCompany
          ? []
          : normalizedReferenceData["furtherDeductionS14n"] || [],
        [FURTHER_DEDUCTION_OTHER]: isInvestmentCompany
          ? []
          : normalizedReferenceData["furtherDeductionOther"] || [],
        "Current Year Capital Allowances":
          normalizedReferenceData["currentYearCapitalAllowances"] || [],
        "Unutilised Capital Allowances": isInvestmentCompany
          ? []
          : sortedUnutilisedCapitalAllowances,
        "Unutilised Losses": isInvestmentCompany ? [] : sortedUnutilisedLosses,
        "Unutilised Donations": isInvestmentCompany
          ? []
          : sortedUnutilisedDonations,
        "Other Source Income": isInvestmentCompany
          ? []
          : normalizedReferenceData["otherSourceIncome"] || [],
        "Current Year Donations":
          normalizedReferenceData["currentYearDonations"] || [],
        "Current Year Investment Losses":
          normalizedReferenceData["currentYearInvestmentLosses"] || [],
        "Partial Tax Exemption / Full Tax Exemption": taxExemptionAmount,
        "Corporate Income Tax Rebate": corporateIncomeTaxRebate,
        "Tax Previously Assessed": normalizedTaxPreviouslyAssessed,
        currentYearLosses: currentYearLosses || 0,
        adjustedProfitAfterTaxAdjustments:
          computedSummary.adjustedProfitAfterTaxAdjustments || 0,
        unutilisedCAcf: computedSummary.unutilisedCAcf || 0,
        unutilisedLosscf: computedSummary.unutilisedLosscf || 0,
        unutilisedDonationcf: computedSummary.unutilisedDonationcf || 0,
        isStartup: isStartup,
        comments: comments,
        reviewNote: reviewNote,
        doc: JSON.stringify({
          company,
          currency,
          currencyRate,
          netProfitBeforeTax,
          referenceData: normalizedReferenceData,
          unutilisedCapitalAllowances: sortedUnutilisedCapitalAllowances,
          unutilisedLosses: sortedUnutilisedLosses,
          currentYearLosses,
          unutilisedDonations: sortedUnutilisedDonations,
          corporateIncomeTaxRebate,
          currentYearRebate: activeCurrentYearRebate,
          isStartup,
          taxPreviouslyAssessed: normalizedTaxPreviouslyAssessed,
          computedSummary,
          unabsorbedSummary: buildUnabsorbedSummary(),
          crossYAApportionmentContext,
          comments,
          reviewNote
        })
      };

      updateMainComputation(mainComputationSaveData);
      handleSuccess("Successfully saved the Main Computation.");
      recordSaved();
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUnutilisedData = (id) => {
    const newRow = {
      yearOfAssessment: "",
      amount: "0",
      reference: "",
      schedule: "",
      exchangeRate: currencyRate,
      historyAmount: [],
      ...(isCrossYA
        ? {
            preceding_amount: 0,
            current_amount: 0,
            precedingHistoryAmount: [0],
            currentHistoryAmount: [0]
          }
        : {}),
      isForfeited: false,
      toDeclareAgreement: false,
      agreementStatus: {
        dateOfNOA: null,
        isAgreed: null
      }
    };

    switch (id) {
      case UN_CA:
        setUnutilisedCapitalAllowances((prev) => [...prev, newRow]);
        recordChangeHappened();
        return;
      case UN_LOSS:
        setUnutilisedLosses((prev) => [...prev, newRow]);
        recordChangeHappened();
        return;
      case UN_DONATION:
        setUnutilisedDonations((prev) => [...prev, newRow]);
        recordChangeHappened();
        return;
      default:
        return;
    }
  };

  const handleUploadPriorYARows = (rows) => {
    if (!canEdit || isInvestmentCompany || !rows.length) return;
    const setters = {
      CA: setUnutilisedCapitalAllowances,
      Losses: setUnutilisedLosses,
      Donations: setUnutilisedDonations
    };
    Object.entries(setters).forEach(([section, setRows]) => {
      const sectionRows = rows
        .filter((row) => row.section === section)
        .map(({ section: uploadSection, ...row }) => row);
      if (sectionRows.length) setRows((prev) => [...prev, ...sectionRows]);
    });
    recordChangeHappened();
  };

  const handleRemoveUnutilisedData = (id, index) => {
    const removeByIndex = (prev) => {
      const targetRow = prev[index];
      if (targetRow?.isForfeited) {
        return prev;
      }
      return prev.filter((_, i) => i !== index);
    };

    switch (id) {
      case UN_CA:
        setUnutilisedCapitalAllowances(removeByIndex);
        recordChangeHappened();
        return;
      case UN_LOSS:
        setUnutilisedLosses(removeByIndex);
        recordChangeHappened();
        return;
      case UN_DONATION:
        setUnutilisedDonations(removeByIndex);
        recordChangeHappened();
        return;
      default:
        return;
    }
  };

  const handleUpdateUnutilisedData = (id, data) => {
    const { index, field, value } = data;
    const agreementStatusFields = ["isAgreed", "dateOfNOA"];

    const getUpdatedRow = (row) => {
      if (field === "amount") {
        const amount = sumCalculatorValues(value);
        if (isCrossYA) {
          return {
            ...row,
            amount,
            historyAmount: value,
            preceding_amount: 0,
            current_amount: amount,
            precedingHistoryAmount: [0],
            currentHistoryAmount: value
          };
        }

        return normalizeSingleYARow({
          ...row,
          amount,
          historyAmount: value
        });
      }
      if (field === "preceding_amount" || field === "current_amount") {
        const historyField =
          field === "preceding_amount"
            ? "precedingHistoryAmount"
            : "currentHistoryAmount";
        const nextFieldAmount = sumCalculatorValues(value);
        const precedingAmount =
          field === "preceding_amount"
            ? nextFieldAmount
            : parseFloat(row?.preceding_amount) || 0;
        const currentAmount =
          field === "current_amount"
            ? nextFieldAmount
            : parseFloat(row?.current_amount) || 0;
        const totalAmount = precedingAmount + currentAmount;
        const nextRow = {
          ...row,
          amount: totalAmount,
          historyAmount: [totalAmount],
          preceding_amount: precedingAmount,
          current_amount: currentAmount,
          [historyField]: value,
          crossYAOffset: 0,
          crossYAOffsetAppliedTo: "",
          crossYAMismatchAmount: 0
        };

        return nextRow;
      }
      if (field === "toDeclareAgreement") {
        return {
          ...row,
          toDeclareAgreement: value,
          agreementStatus: value
            ? {
                dateOfNOA:
                  row.agreementStatus?.dateOfNOA ?? row.dateOfNOA ?? "",
                isAgreed: row.agreementStatus?.isAgreed ?? row.isAgreed ?? false
              }
            : {
                dateOfNOA: null,
                isAgreed: null
              }
        };
      }
      if (agreementStatusFields.includes(field)) {
        if (row.toDeclareAgreement !== true) {
          return {
            ...row,
            agreementStatus: {
              dateOfNOA: null,
              isAgreed: null
            }
          };
        }

        return {
          ...row,
          agreementStatus: {
            dateOfNOA: row.agreementStatus?.dateOfNOA ?? row.dateOfNOA ?? "",
            isAgreed: row.agreementStatus?.isAgreed ?? row.isAgreed ?? false,
            ...row.agreementStatus,
            [field]: value
          }
        };
      }
      return {
        ...row,
        [field]: value
      };
    };

    const updateArray = (row, i) => {
      if (i !== index) return row;
      if (row?.isForfeited) return row;
      return getUpdatedRow(row);
    };

    switch (id) {
      case UN_CA:
        setUnutilisedCapitalAllowances((prev) => prev.map(updateArray));
        recordChangeHappened();
        break;
      case UN_LOSS:
        setUnutilisedLosses((prev) => prev.map(updateArray));
        recordChangeHappened();
        break;
      case UN_DONATION:
        setUnutilisedDonations((prev) => prev.map(updateArray));
        recordChangeHappened();
        break;
      default:
        break;
    }
  };

  const handleForfeitUnutilisedData = (id, index) => {
    const toggleForfeit = (prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, isForfeited: !row?.isForfeited } : row
      );

    switch (id) {
      case UN_CA:
        setUnutilisedCapitalAllowances(toggleForfeit);
        recordChangeHappened();
        return;
      case UN_LOSS:
        setUnutilisedLosses(toggleForfeit);
        recordChangeHappened();
        return;
      case UN_DONATION:
        setUnutilisedDonations(toggleForfeit);
        recordChangeHappened();
        return;
      default:
        return;
    }
  };

  const handleUpdateStartup = (yaKey, value) => {
    if (!isCrossYA) {
      setIsStartup((prev) =>
        typeof value === "boolean" ? value : !Boolean(prev)
      );
      recordChangeHappened();
      return;
    }

    setIsStartup((prev) => ({
      ...buildDefaultStartupConfig(true),
      ...(prev || {}),
      [yaKey]: Boolean(value)
    }));
    recordChangeHappened();
  };

  const handleUpdateCorporateData = (field, value, yaKey) => {
    const nextValue = typeof value === "boolean" ? value : getCommaOut(value);

    setCorporateIncomeTaxRebate((prev) => {
      if (!isCrossYA) {
        return {
          ...normaliseCorporateRebateConfig(prev, false),
          [field]: nextValue
        };
      }

      const targetYAKey = yaKey || "current";
      return {
        ...buildDefaultCorporateRebate(true),
        ...(prev || {}),
        [targetYAKey]: {
          ...defaultCorporateRebateConfig,
          ...(prev?.[targetYAKey] || {}),
          [field]: nextValue
        }
      };
    });
    recordChangeHappened();
  };

  const handleUpdateTaxDeductions = (field, value, index) => {
    setTaxPreviouslyAssessed((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;

        if (field === "amount") {
          return normalizeSingleYARow({
            ...item,
            amount: sumCalculatorValues(value),
            historyAmount: value
          });
        }

        if (field === "preceding_amount" || field === "current_amount") {
          const historyField =
            field === "preceding_amount"
              ? "precedingHistoryAmount"
              : "currentHistoryAmount";
          const nextItem = {
            ...item,
            [field]: sumCalculatorValues(value),
            [historyField]: value
          };

          return reconcileCrossYAAmounts(nextItem);
        }

        if (field === "crossYAOffset") {
          return isCrossYA ? applyCrossYAOffset(item, value) : item;
        }

        return {
          ...item,
          [field]: value
        };
      })
    );
    recordChangeHappened();
  };

  const handleReplaceTaxPreviouslyAssessedRows = (manualRows = []) => {
    setTaxPreviouslyAssessed((prev) => {
      const autoloadRows = prev.filter((row) => row?.status === "autoload");
      const nextManualRows =
        manualRows.length > 0
          ? manualRows
          : isCrossYA
            ? [
                {
                  date: "",
                  amount: 0,
                  historyAmount: [],
                  preceding_amount: 0,
                  current_amount: 0,
                  precedingHistoryAmount: [],
                  currentHistoryAmount: []
                }
              ]
            : [
                {
                  date: "",
                  amount: 0,
                  historyAmount: []
                }
              ];

      if (!isCrossYA) {
        return normalizeSingleYATaxPreviouslyAssessedRows([
          ...nextManualRows,
          ...autoloadRows
        ]);
      }

      return [
        ...nextManualRows.map((row) =>
          reconcileCrossYAAmounts({
            ...row,
            amount: getTaxPreviouslyAssessedAmount(row, true)
          })
        ),
        ...autoloadRows
      ];
    });
    recordChangeHappened();
  };

  const handleUpdateReferenceData = (id, newData, index) => {
    const key = getDataKey(id);

    setReferenceData((prev) => {
      const updatedArray = [...(prev[key] ?? [])];

      if (index < 0 || index >= updatedArray.length) {
        return prev;
      }

      const updatedRow = {
        ...updatedArray[index],
        ...newData
      };
      updatedArray[index] =
        isCrossYA &&
        Object.prototype.hasOwnProperty.call(newData, "crossYAOffset")
          ? applyCrossYAOffset(updatedArray[index], newData.crossYAOffset)
          : isCrossYA
            ? reconcileCrossYAAmounts(updatedRow)
            : normalizeSingleYARow(updatedRow);

      return {
        ...prev,
        [key]: updatedArray
      };
    });
    recordChangeHappened();
  };

  const handleCommentsChange = (e) => {
    setComments(e.target.value);
    recordChangeHappened();
  };

  const handleReviewNoteChange = (e) => {
    setReviewNote(e.target.value);
    recordChangeHappened();
  };

  const onNavigate = (section) => {
    const sectionId =
      typeof section === "string" ? section : section.parentId || section.id;
    const sectionElement = document.getElementById(sectionId);
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: "smooth" });
    }

    if (typeof section !== "string" && section.parentId) {
      window.dispatchEvent(
        new CustomEvent("main-tax-computation:select-adjustment-tab", {
          detail: {
            containerId: section.parentId,
            tabId: section.id
          }
        })
      );
    }
  };

  return (
    <Box sx={{ padding: 2 }}>
      {priorYAUploadOpen && canEdit && !isInvestmentCompany && (
        <PriorYAExcelUpload
          key={`${company.yearOfAssessment}-${isCrossYA}-${currency}-${currencyRate}`}
          company={company}
          currency={currency}
          currencyRate={currencyRate}
          isCrossYA={isCrossYA}
          onUpload={handleUploadPriorYARows}
          onClose={() => setPriorYAUploadOpen(false)}
        />
      )}
      <SectionNavigation
        company={company}
        sections={PageSections}
        onNavigate={onNavigate}
        toggle={() => setOpenNavList((prev) => !prev)}
        openNavList={openNavList}
      />

      <Header
        canEdit={canEdit}
        company={company}
        handleSave={handleSave}
        onExcelUpload={
          isInvestmentCompany ? undefined : () => setPriorYAUploadOpen(true)
        }
        currency={currency}
        crossYAApportionmentContext={crossYAApportionmentContext}
      >
        <PdfGenerator
          company={company}
          currency={currency}
          currencyRate={currencyRate}
          netProfitBeforeTax={netProfitBeforeTax}
          referenceData={referenceData}
          unutilisedCapitalAllowances={
            isInvestmentCompany
              ? []
              : sortUnutilisedRowsByYear(unutilisedCapitalAllowances)
          }
          unutilisedLosses={
            isInvestmentCompany
              ? []
              : sortUnutilisedRowsByYear(unutilisedLosses)
          }
          currentYearLosses={currentYearLosses}
          unutilisedDonations={
            isInvestmentCompany
              ? []
              : sortUnutilisedRowsByYear(unutilisedDonations)
          }
          corporateIncomeTaxRebate={corporateIncomeTaxRebate}
          currentYearRebate={activeCurrentYearRebate}
          isStartup={isStartup}
          taxPreviouslyAssessed={taxPreviouslyAssessed}
          computedSummary={computedSummary}
          getUnabsorbedSummary={buildUnabsorbedSummary}
          crossYAApportionmentContext={crossYAApportionmentContext}
          comments={comments}
          reviewNote={reviewNote}
        />
      </Header>

      <br />

      {Object.entries(PageSections).map(([group, items]) => {
        if (isInvestmentCompany) {
          // Investment company rendering logic
          if (["A", "B"].includes(group))
            // if (["A", "B", "C"].includes(group))
            return items.map((item) => (
              <AdjustmentDetails
                key={item.id}
                id={item.id}
                dataId={item.value}
                currency={currency}
                currencyRate={currencyRate}
                data={referenceData[getDataKey(item.value)]}
                title={item.label}
                totalAmount={getSectionTotal(item.totalId)}
                onUpdateReference={handleUpdateReferenceData}
                canEdit={canEdit}
                isCrossYA={isCrossYA}
              />
            ));

          if (["C"].includes(group))
            return items.map((item) => (
              <TaxExemptionDetails
                key={item.id}
                id={item.id}
                dataId={item.id}
                data={computedSummary}
                isStartup={isStartup}
                onUpdateStartup={handleUpdateStartup}
                totalAmount={-Math.abs(taxExemptionAmount)}
                loading={summaryLoading}
                disabled={!canEdit}
                canEdit={canEdit}
                isInvestmentCompany={isInvestmentCompany}
                isCrossYA={isCrossYA}
                company={company}
              />
            ));

          if (["D"].includes(group))
            return items.map((item) =>
              item.id === "corporate_income_tax_rebate" ? (
                <CorporateRebate
                  key={item.id}
                  id={item.id}
                  dataId={item.id}
                  data={corporateIncomeTaxRebate}
                  onUpdate={handleUpdateCorporateData}
                  totalAmount={corporateIncomeRebateAmount}
                  currentYearRebate={activeCurrentYearRebate}
                  company={company}
                  loading={summaryLoading}
                  disabled={!canEdit}
                  canEdit={canEdit}
                  isInvestmentCompany={isInvestmentCompany}
                  isCrossYA={isCrossYA}
                />
              ) : (
                <TaxPreviouslyAssessed
                  key={item.id}
                  id={item.id}
                  dataId={item.id}
                  data={taxPreviouslyAssessed}
                  onUpdate={handleUpdateTaxDeductions}
                  onReplaceRows={handleReplaceTaxPreviouslyAssessedRows}
                  totalAmount={getSectionTotal(item.totalId)}
                  disabled={!canEdit}
                  canEdit={canEdit}
                  currency={currency}
                  currencyRate={currencyRate}
                  isCrossYA={isCrossYA}
                />
              )
            );
        } else {
          // Common company rendering logic
          if (["A", "B"].includes(group) && items.length > 1)
            return (
              <AdjustmentDetails
                key={items[0].parentId}
                id={items[0].parentId}
                title={adjustmentGroupTitle[group]}
                totalAmount={items.reduce(
                  (sum, item) => sum + getSectionTotal(item.totalId),
                  0
                )}
                tabs={items.map((item) => ({
                  id: item.id,
                  label: item.label,
                  dataId: item.value,
                  data: referenceData[getDataKey(item.value)] || [],
                  totalAmount: getSectionTotal(item.totalId)
                }))}
                onUpdateReference={handleUpdateReferenceData}
                canEdit={canEdit}
                currency={currency}
                currencyRate={currencyRate}
                isCrossYA={isCrossYA}
              />
            );

          if (["A", "B", "C", "E", "F"].includes(group))
            return items.map((item) => (
              <AdjustmentDetails
                key={item.id}
                id={item.id}
                dataId={item.value}
                data={referenceData[getDataKey(item.value)]}
                title={item.label}
                totalAmount={getSectionTotal(item.totalId)}
                onUpdateReference={handleUpdateReferenceData}
                canEdit={canEdit}
                currency={currency}
                currencyRate={currencyRate}
                isCrossYA={isCrossYA}
              />
            ));

          if (["D"].includes(group))
            return items.map((item) => (
              <UnutilisedDetails
                key={item.id}
                id={item.id}
                currency={currency}
                currencyRate={currencyRate}
                company={company}
                dataId={item.id}
                data={getUnutilisedDatasetById(item.value)}
                currentYearLosses={currentYearLosses}
                isCrossYA={isCrossYA}
                onAdd={() => handleAddUnutilisedData(item.value)}
                onUpdate={(value) =>
                  handleUpdateUnutilisedData(item.value, value)
                }
                onRemove={(index) =>
                  handleRemoveUnutilisedData(item.value, index)
                }
                onForfeit={(index) =>
                  handleForfeitUnutilisedData(item.value, index)
                }
                title={item.label}
                showAddButton={true}
                totalAmount={getSectionTotal(item.totalId)}
                canEdit={canEdit}
              />
            ));

          if (["G"].includes(group))
            return items.map((item) => (
              <TaxExemptionDetails
                key={item.id}
                id={item.id}
                dataId={item.id}
                data={computedSummary}
                isStartup={isStartup}
                onUpdateStartup={handleUpdateStartup}
                totalAmount={-Math.abs(taxExemptionAmount)}
                loading={summaryLoading}
                disabled={!canEdit}
                canEdit={canEdit}
                isCrossYA={isCrossYA}
                company={company}
              />
            ));

          if (["H"].includes(group))
            return items.map((item) =>
              item.id === "corporate_income_tax_rebate" ? (
                <CorporateRebate
                  key={item.id}
                  id={item.id}
                  dataId={item.id}
                  data={corporateIncomeTaxRebate}
                  onUpdate={handleUpdateCorporateData}
                  totalAmount={corporateIncomeRebateAmount}
                  currentYearRebate={activeCurrentYearRebate}
                  company={company}
                  loading={summaryLoading}
                  disabled={!canEdit}
                  canEdit={canEdit}
                  isInvestmentCompany={isInvestmentCompany}
                  isCrossYA={isCrossYA}
                />
              ) : (
                <TaxPreviouslyAssessed
                  key={item.id}
                  id={item.id}
                  dataId={item.id}
                  data={taxPreviouslyAssessed}
                  onUpdate={handleUpdateTaxDeductions}
                  onReplaceRows={handleReplaceTaxPreviouslyAssessedRows}
                  totalAmount={getSectionTotal(item.totalId)}
                  disabled={!canEdit}
                  canEdit={canEdit}
                  currency={currency}
                  currencyRate={currencyRate}
                  isCrossYA={isCrossYA}
                />
              )
            );
        }
        return null;
      })}

      <CalculationSummary
        company={company}
        corporateIncomeTaxRebate={corporateIncomeTaxRebate}
        isInvestmentCompany={isInvestmentCompany}
        isStartup={isStartup}
        netProfitBeforeTax={netProfitBeforeTax}
        computedSummary={computedSummary}
        loading={summaryLoading}
        currency={currency}
        currencyRate={currencyRate}
        masCurrencyRate={masCurrencyRate}
        crossYAApportionmentContext={crossYAApportionmentContext}
        currentYearRebate={activeCurrentYearRebate}
        referenceData={referenceData}
        taxPreviouslyAssessed={taxPreviouslyAssessed}
        unutilisedCapitalAllowances={unutilisedCapitalAllowances}
        unutilisedDonations={unutilisedDonations}
        unutilisedLosses={unutilisedLosses}
      />
      <Box>
        <Notes
          label='Notes/comments'
          value={comments}
          onChange={handleCommentsChange}
          enableEdit={canEdit}
        />
        <Notes
          label='Internal Review Notes'
          value={reviewNote}
          onChange={handleReviewNoteChange}
          enableEdit={canEdit}
        />
      </Box>
    </Box>
  );
});

export default MainTaxComputation;
